import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Target, TrendingUp, Users as UsersIcon, Building2, RefreshCw } from "lucide-react";

const fmtGBP = (n) =>
  n == null ? "—"
    : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(Number(n));

/**
 * Sub-label under the "MRR closed won" progress row.
 * Xentra policy (20 Jul 2026): sales target credits NEW MRR + the deal
 * owner's manually-entered uplift (`manual_uplift_mrr` field on the deal).
 * Full renewal MRR does NOT credit the target.
 */
function _attrSub(row) {
  if (!row) return null;
  const newV = Number(row.mrr_new_actual || 0);
  const upV = Number(row.mrr_manual_uplift_actual ?? row.mrr_renewal_uplift_actual ?? 0);
  const parts = [];
  if (newV > 0) parts.push(`New ${fmtGBP(newV)}`);
  if (upV > 0) parts.push(`Uplift ${fmtGBP(upV)}`);
  if (parts.length === 0) return null;
  return `Credit = New + Manual uplift · ${parts.join(" · ")}`;
}

export default function SalesTargetsTab() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/dashboard/sales-targets?period=${period}`);
      setData(r.data);
    })();
  }, [period]);

  if (!data) return <div className="text-slate-500 font-mono text-sm">Loading targets…</div>;

  const business = data.business;
  const teams = data.teams || [];
  const users = data.users || []; // backend already filters to users with at least one target

  const hasAnything = !!business || teams.length > 0 || users.length > 0;

  return (
    <div className="space-y-6" data-testid="sales-targets-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700 flex items-center gap-1.5">
            <Target className="h-3 w-3" /> Sales Targets
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-1">{data.period_label || data.period_key}</h2>
          <p className="text-xs text-slate-500">{data.start_date} → {data.end_date}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[["month", "This month"], ["quarter", "This FY quarter"], ["ytd", "YTD (FY)"], ["year", "This year"]].map(([v, l]) => (
            <button
              key={v}
              data-testid={`targets-period-${v}`}
              onClick={() => setPeriod(v)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                period === v ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
            >{l}</button>
          ))}
        </div>
      </div>

      {/* Fiscal-year pacing indicator (only shown for YTD view) */}
      {period === "ytd" && data.fy_info && (
        <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-4 flex items-center gap-4" data-testid="fy-pacing">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-blue-700">Fiscal year</div>
            <div className="font-heading text-lg text-slate-900">{data.fy_info.fy_label}</div>
            <div className="text-xs text-slate-500 font-mono">{data.fy_info.fy_start} → {data.fy_info.fy_end}</div>
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
              <span>FY elapsed</span>
              <span className="font-mono font-semibold text-blue-700" data-testid="fy-elapsed-pct">
                {data.fy_info.elapsed_pct}% · day {data.fy_info.elapsed_days} of {data.fy_info.total_days}
              </span>
            </div>
            <div className="h-2 bg-white rounded-full overflow-hidden border border-blue-100">
              <div className="h-full bg-blue-500 transition-all" style={{ width: `${Math.min(data.fy_info.elapsed_pct, 100)}%` }} />
            </div>
            <div className="text-[10px] font-mono text-slate-500 mt-1.5">
              Progress bars below compare YTD actuals against the FULL fiscal-year target. On-pace = bar close to the FY-elapsed line above.
            </div>
          </div>
        </div>
      )}

      {!hasAnything && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardContent className="p-6 text-sm text-slate-700">
            No targets assigned yet for this period. Set them in <b>Admin → Sales Targets</b>.
          </CardContent>
        </Card>
      )}

      {/* Business target */}
      {business && (
        <div data-testid="business-target-section">
          <SectionHeading icon={Building2} label="Business" sub="Aggregate across all deals" />
          <Card className="border-slate-200 mt-3" data-testid="business-target-card">
            <CardHeader className="border-b border-slate-100 py-3 flex flex-row items-center justify-between">
              <CardTitle className="text-base font-heading">All deals · whole business</CardTitle>
              <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">Business</Badge>
            </CardHeader>
            <CardContent className="p-5 space-y-4">
              {business.mrr_target != null && (
                <ProgressRow
                  icon={TrendingUp}
                  label="MRR closed won"
                  actual={business.mrr_actual}
                  target={business.mrr_target}
                  fmt={fmtGBP}
                  derived={business.mrr_derived}
                  derivedFrom={business.mrr_derived_from}
                  subLabel={_attrSub(business)}
                />
              )}
              {business.lead_count_target != null && (
                <ProgressRow icon={UsersIcon} label="Leads generated" actual={business.lead_count_actual} target={business.lead_count_target} fmt={(n) => String(n)} derived={business.lead_count_derived} derivedFrom={business.lead_count_derived_from} />
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team targets */}
      {teams.length > 0 && (
        <div data-testid="team-targets-section">
          <SectionHeading icon={UsersIcon} label="Sales Teams" sub="Grouped by people" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-3">
            {teams.map((t) => (
              <Card key={t.team_name} className="border-slate-200" data-testid={`team-card-${t.team_name}`}>
                <CardHeader className="border-b border-slate-100 py-3 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-heading">{t.team_name}</CardTitle>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mt-0.5">
                      {t.member_count} member{t.member_count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Team</Badge>
                </CardHeader>
                <CardContent className="p-5 space-y-4">
                  {t.team_user_names?.length > 0 && (
                    <div className="text-xs text-slate-500">
                      <span className="font-mono uppercase text-[9px] mr-1">Members:</span>
                      {t.team_user_names.join(", ")}
                    </div>
                  )}
                  {t.mrr_target != null && (
                    <ProgressRow
                      icon={TrendingUp}
                      label="MRR closed won"
                      actual={t.mrr_actual}
                      target={t.mrr_target}
                      fmt={fmtGBP}
                      derived={t.mrr_derived}
                      derivedFrom={t.mrr_derived_from}
                      subLabel={_attrSub(t)}
                    />
                  )}
                  {t.lead_count_target != null && (
                    <ProgressRow icon={UsersIcon} label="Leads generated" actual={t.lead_count_actual} target={t.lead_count_target} fmt={(n) => String(n)} derived={t.lead_count_derived} derivedFrom={t.lead_count_derived_from} />
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Individual targets */}
      {users.length > 0 && (
        <div data-testid="individual-targets-section">
          <SectionHeading icon={Target} label="Individuals" sub="Per salesperson" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-3" data-testid="targets-grid">
            {users.map((u) => (
              <Card key={u.user_id} className="border-slate-200" data-testid={`target-card-${u.user_id}`}>
                <CardHeader className="border-b border-slate-100 py-3 flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-heading">{u.user_name}</CardTitle>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mt-0.5">{u.role || "user"}</div>
                  </div>
                </CardHeader>
                <CardContent className="p-5 space-y-4">
                  {u.lead_count_target != null && (
                    <ProgressRow icon={UsersIcon} label="Leads generated" actual={u.lead_count_actual} target={u.lead_count_target} fmt={(n) => String(n)} derived={u.lead_count_derived} derivedFrom={u.lead_count_derived_from} />
                  )}
                  {u.mrr_target != null && (
                    <ProgressRow
                      icon={TrendingUp}
                      label="MRR closed won"
                      actual={u.mrr_actual}
                      target={u.mrr_target}
                      fmt={fmtGBP}
                      derived={u.mrr_derived}
                      derivedFrom={u.mrr_derived_from}
                      subLabel={_attrSub(u)}
                    />
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeading({ icon: Icon, label, sub }) {
  return (
    <div className="flex items-end justify-between border-b border-slate-200 pb-2">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 flex items-center gap-1.5">
          <Icon className="h-3 w-3" /> {label}
        </div>
        {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function ProgressRow({ icon: Icon, label, actual, target, fmt, derived, derivedFrom, subLabel, info }) {
  const pct = target ? Math.min(150, (actual / target) * 100) : 0;
  const onTrack = target && pct >= 80;
  const ahead = target && pct >= 100;
  const bar = ahead ? "bg-emerald-500" : onTrack ? "bg-blue-500" : "bg-amber-500";
  const ringClass = target == null ? "text-slate-400" : ahead ? "text-emerald-600" : onTrack ? "text-blue-600" : "text-amber-600";
  const derivedLabel = derived ? (
    derivedFrom === "year" ? "from yearly ÷12"
      : derivedFrom === "quarter" ? "from quarterly ÷3"
      : derivedFrom === "month_sum" ? "sum of months"
      : derivedFrom === "quarter_sum" ? "sum of quarters"
      : derivedFrom === "fy_month_sum" ? "sum of FY months"
      : derivedFrom === "fy_quarter_sum" ? "sum of FY quarters"
      : (derivedFrom && derivedFrom.startsWith("year_")) ? `from year ${derivedFrom.slice(5)}`
      : "derived"
  ) : null;
  return (
    <div data-testid={`progress-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <div className="flex items-center gap-2 text-sm text-slate-700 min-w-0">
          <Icon className={`h-4 w-4 shrink-0 ${ringClass}`} />
          <span className="truncate">{label}</span>
          {info && (
            <span
              className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-50 border border-slate-200 text-slate-500 shrink-0"
              title="Informational — no target set for this metric"
              data-testid="info-badge"
            >
              info
            </span>
          )}
          {derived && (
            <span
              className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-500 shrink-0"
              title={`Target derived ${derivedLabel} — no target set at this granularity`}
              data-testid="derived-badge"
            >
              {derivedLabel}
            </span>
          )}
        </div>
        <div className="text-sm font-mono text-right">
          <span className="font-bold text-slate-900">{fmt(actual)}</span>
          {target != null && <>
            <span className="text-slate-400 mx-1">/</span>
            <span className="text-slate-500">{fmt(target)}</span>
            <span className={`ml-2 font-semibold ${ringClass}`}>{pct.toFixed(0)}%</span>
          </>}
        </div>
      </div>
      {subLabel && (
        <div className="text-[10px] font-mono text-slate-400 mb-1.5 pl-6">{subLabel}</div>
      )}
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        {target != null && (
          <div className={`h-full ${bar} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
        )}
      </div>
    </div>
  );
}
